import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Download, ExternalLink, FileText, Package, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Header } from '../components/Header';
import { useTranslation } from '../contexts/TranslationContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Product-family display labels (brand terms — kept as-is across languages).
const PRODUCT_LABELS = { vinyl: 'Vinyl', cd: 'CD', dvd: 'DVD', bluray: 'Blu-ray' };
const PRODUCT_ORDER = ['vinyl', 'cd', 'dvd', 'bluray'];

const fmtBytes = (n) => {
    if (!n && n !== 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export default function ArtworkTemplatesLibraryPage() {
    const { t } = useTranslation();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [productFilter, setProductFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [categoryOptions, setCategoryOptions] = useState([]);

    const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

    // Load admin-managed categories (active), filtered by product type.
    useEffect(() => {
        (async () => {
            try {
                const qp = productFilter ? `?product_type=${productFilter}` : '';
                const { data } = await axios.get(`${API}/api/artwork-templates/managed-categories${qp}`, { headers: authHeaders() });
                // Dedupe by name when no product type is selected (same name
                // can exist under multiple product families).
                const seen = new Set();
                const uniq = (data || []).filter(c => {
                    if (seen.has(c.name)) return false;
                    seen.add(c.name);
                    return true;
                });
                setCategoryOptions(uniq);
            } catch { setCategoryOptions([]); }
        })();
        setCategoryFilter('');
    }, [productFilter]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams();
                if (productFilter) params.set('product_type', productFilter);
                if (categoryFilter) params.set('category', categoryFilter);
                if (search) params.set('search', search);
                const { data } = await axios.get(`${API}/api/artwork-templates?${params}`, { headers: authHeaders() });
                setTemplates(data || []);
            } catch (e) {
                toast.error(e.response?.data?.detail || t('templates.load_error', 'Failed to load templates'));
            } finally {
                setLoading(false);
            }
        })();
    }, [productFilter, categoryFilter, search, t]);

    const grouped = useMemo(() => {
        const g = {};
        for (const tpl of templates) {
            const p = tpl.product_type || 'other';
            const cat = tpl.category || t('templates.uncategorised', 'Uncategorized');
            if (!g[p]) g[p] = {};
            if (!g[p][cat]) g[p][cat] = [];
            g[p][cat].push(tpl);
        }
        return g;
    }, [templates, t]);

    const handleDownloadFile = async (tplId, fileId) => {
        try {
            const { data } = await axios.get(`${API}/api/artwork-templates/${tplId}/files/${fileId}/download`, { headers: authHeaders() });
            window.open(data.download_url, '_blank');
        } catch (e) {
            toast.error(t('templates.download_error', 'Download failed'));
        }
    };

    const handlePreview = async (tpl) => {
        try {
            const { data } = await axios.get(`${API}/api/artwork-templates/${tpl.id}/preview`, { headers: authHeaders() });
            window.open(data.preview_url, '_blank');
        } catch (e) {
            toast.error(t('templates.preview_error', 'Preview not available'));
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <div className="max-w-6xl mx-auto py-6 px-4">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-900" data-testid="templates-library-heading">
                    {t('templates.title', 'Artwork Templates')}
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    {t('templates.subtitle', 'Print-ready templates for labels, sleeves, digipaks and booklets. Each template contains one or more files — download only what you need.')}
                </p>
            </div>

            <Card className="mb-6">
                <CardContent className="pt-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="relative flex-1 min-w-[220px]">
                            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <Input
                                data-testid="library-search"
                                placeholder={t('templates.search_placeholder', 'Search templates…')}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                        <select
                            value={productFilter}
                            onChange={e => setProductFilter(e.target.value)}
                            className="border rounded px-3 py-2 text-sm bg-white h-10"
                            data-testid="library-filter-product"
                        >
                            <option value="">{t('templates.filter_all_products', 'All products')}</option>
                            {PRODUCT_ORDER.map(p => <option key={p} value={p}>{PRODUCT_LABELS[p]}</option>)}
                        </select>
                        <select
                            value={categoryFilter}
                            onChange={e => setCategoryFilter(e.target.value)}
                            className="border rounded px-3 py-2 text-sm bg-white h-10 disabled:opacity-50"
                            data-testid="library-filter-category"
                            disabled={categoryOptions.length === 0}
                        >
                            <option value="">{t('templates.filter_all_categories', 'All categories')}</option>
                            {categoryOptions.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </select>
                    </div>
                </CardContent>
            </Card>

            {loading ? (
                <div className="text-center py-10 text-slate-500">{t('common.loading_ellipsis', 'Loading…')}</div>
            ) : templates.length === 0 ? (
                <div className="text-center py-16 text-slate-500">
                    <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>{t('templates.empty', 'No templates found. Try adjusting the filter.')}</p>
                </div>
            ) : (
                <div className="space-y-8">
                    {PRODUCT_ORDER.filter(p => grouped[p]).map(product => (
                        <div key={product} data-testid={`product-section-${product}`}>
                            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                                <Package className="h-5 w-5 text-indigo-600" /> {PRODUCT_LABELS[product]}
                            </h2>
                            {Object.keys(grouped[product]).sort().map(category => (
                                <div key={category} className="mb-6">
                                    <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-2">{category}</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {grouped[product][category].map(tpl => (
                                            <TemplateCard
                                                key={tpl.id}
                                                tpl={tpl}
                                                onDownloadFile={handleDownloadFile}
                                                onPreview={handlePreview}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}
            </div>
        </div>
    );
}

function TemplateCard({ tpl, onDownloadFile, onPreview }) {
    const { t } = useTranslation();
    const files = tpl.files || [];
    const multiple = files.length > 1;
    const [expanded, setExpanded] = useState(!multiple);
    // Customer-facing heading: use the human-readable Description; fall back
    // to Name when empty. Never surface internal IDs on customer cards.
    const heading = (tpl.description && tpl.description.trim()) ? tpl.description.trim() : tpl.name;

    return (
        <div className="border rounded-lg p-3 bg-white hover:shadow-sm transition-shadow flex flex-col" data-testid={`template-card-${tpl.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 line-clamp-2 break-words" title={heading} data-testid={`template-card-heading-${tpl.id}`}>{heading}</div>
                    {tpl.subcategory && <div className="text-xs text-slate-500 mt-0.5 break-words">{tpl.subcategory}</div>}
                </div>
                <Badge variant="outline" className="text-[10px] uppercase shrink-0">
                    {files.length === 1
                        ? t('templates.file_count_one', '1 file')
                        : t('templates.file_count_many', '{n} files', { n: files.length })}
                </Badge>
            </div>
            <div className="flex items-center justify-between gap-2 text-[11px] text-slate-400 mb-3">
                <span title={tpl.updated_at}>
                    {t('templates.updated_prefix', 'Updated {date}', { date: (tpl.updated_at || '').split('T')[0] })}
                </span>
                <button
                    onClick={() => onPreview(tpl)}
                    className="inline-flex items-center gap-1 text-indigo-700 hover:underline"
                    data-testid={`library-preview-${tpl.id}`}
                >
                    <ExternalLink className="h-3 w-3" /> {t('templates.preview_link', 'Preview')}
                </button>
            </div>

            {files.length === 0 ? (
                <div className="text-xs text-slate-400 italic">{t('templates.no_files', 'No files uploaded yet.')}</div>
            ) : !expanded ? (
                <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={() => setExpanded(true)}
                    data-testid={`library-show-files-${tpl.id}`}
                >
                    <ChevronDown className="h-4 w-4 mr-1" /> {t('templates.show_n_files', 'Show {n} files', { n: files.length })}
                </Button>
            ) : (
                <div className="space-y-1.5">
                    {files.map(f => (
                        <div key={f.id} className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2 py-1.5" data-testid={`library-file-${f.id}`}>
                            <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <div className="truncate font-medium text-slate-800" title={f.filename}>{f.label || f.filename}</div>
                                {f.label && <div className="text-[10px] text-slate-400 truncate">{f.filename}</div>}
                            </div>
                            <span className="text-[10px] text-slate-400 shrink-0">{fmtBytes(f.size)}</span>
                            <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2"
                                onClick={() => onDownloadFile(tpl.id, f.id)}
                                data-testid={`library-download-${f.id}`}
                                title={t('templates.download_title', 'Download')}
                                aria-label={t('templates.download_title', 'Download')}
                            >
                                <Download className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    ))}
                    {multiple && (
                        <button
                            onClick={() => setExpanded(false)}
                            className="text-[11px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 pt-1"
                        >
                            <ChevronRight className="h-3 w-3" /> {t('templates.collapse', 'Collapse')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
